import React from "react";
import PropTypes from "prop-types";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import {
  TABLE_EMPTY_CELL,
  DEFAULT_TABLE_TEXT_TRUNCATE_MAX,
  truncateTableText,
  externalLinkHref,
} from "../../../utils/tableDisplayUtils";
import "./TableDisplayCells.css";

/**
 * Compact external link for table "Link" columns (opens in new tab).
 */
export function TableLinkCell({
  url,
  onClick,
  linkClassName = "table-display-compact-link",
  label = "Open link",
}) {
  const raw = typeof url === "string" ? url.trim() : "";
  if (!raw) return TABLE_EMPTY_CELL;
  const href = externalLinkHref(raw);
  if (!href) return raw;
  return (
    <a
      className={linkClassName}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={raw}
      onClick={onClick}
    >
      {label}
    </a>
  );
}

TableLinkCell.propTypes = {
  url: PropTypes.string,
  onClick: PropTypes.func,
  linkClassName: PropTypes.string,
  label: PropTypes.string,
};

/**
 * Truncated text with optional tooltip for long values in table cells.
 */
export function TableTextCell({
  text,
  maxLength = DEFAULT_TABLE_TEXT_TRUNCATE_MAX,
  className = "table-display-text-cell",
  wrapClassName = "table-display-tooltip-wrap",
  tooltipContentClassName = "table-display-table-tooltip-content",
  tooltipClassName = "table-display-table-tooltip",
  asButton = false,
  buttonClassName = "table-display-text-btn",
  onClick,
}) {
  const raw = typeof text === "string" ? text.trim() : "";
  if (!raw) {
    return (
      <span className={`${wrapClassName} ${className}`}>{TABLE_EMPTY_CELL}</span>
    );
  }

  const display = truncateTableText(raw, maxLength);
  const needsTooltip = raw.length > maxLength;

  const inner = asButton ? (
    <button type="button" className={buttonClassName} onClick={onClick}>
      {display}
    </button>
  ) : (
    <span className={className}>{display}</span>
  );

  const wrapped = <span className={wrapClassName}>{inner}</span>;

  if (!needsTooltip) return wrapped;

  return (
    <Tooltip
      title={
        <div
          className={tooltipContentClassName}
          onClick={(e) => e.stopPropagation()}
        >
          {raw}
        </div>
      }
      arrow
      placement="top"
      enterDelay={400}
      interactive
      describeChild
      slotProps={{
        tooltip: {
          className: tooltipClassName,
          sx: { maxWidth: 400, p: 0 },
        },
      }}
    >
      {wrapped}
    </Tooltip>
  );
}

TableTextCell.propTypes = {
  text: PropTypes.string,
  maxLength: PropTypes.number,
  className: PropTypes.string,
  wrapClassName: PropTypes.string,
  tooltipContentClassName: PropTypes.string,
  tooltipClassName: PropTypes.string,
  asButton: PropTypes.bool,
  buttonClassName: PropTypes.string,
  onClick: PropTypes.func,
};

/**
 * Task completed checkbox for table "Status" columns (property tasks, homepage assigned tasks).
 */
export function TaskStatusCheckboxCell({
  task,
  checked,
  disabled,
  onChange,
}) {
  return (
    <Checkbox
      size="small"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      sx={{ p: 0.25 }}
      inputProps={{
        "aria-label": `Task ${task?.id ?? ""} completed`,
      }}
    />
  );
}

TaskStatusCheckboxCell.propTypes = {
  task: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};
